import { isAfter } from 'date-fns'
import type { MediaImageFragment, ToccoUser } from '~/api/graphql/generated'
import type { Employee } from '~/views/cms/components/EmployeeSingle.vue'
import type { ToccoFieldData } from '~/types'

const ZONE_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/

/**
 * Parse a Tocco date value, treating a missing timezone designator as UTC
 *
 * @param value The date value as delivered by Drupal
 * @returns The parsed date
 */
export function parseToccoDate(value: string): Date {
  return new Date(ZONE_SUFFIX.test(value) ? value : `${value}Z`)
}

/**
 * Check if registration is enabled based on registration date range
 *
 * @param fieldToccoDataJSON The parsed Tocco data JSON
 * @returns boolean indicating if registration is enabled
 */
export function isRegistrationEnabled(fieldToccoDataJSON: ToccoFieldData): boolean {
  let enabled = false
  if (
    fieldToccoDataJSON.paths.registration_internet_from?.value ||
    fieldToccoDataJSON.paths.registration_internet_till?.value
  ) {
    enabled = true
    if (fieldToccoDataJSON.paths.registration_internet_from?.value) {
      const registrationDateFrom = parseToccoDate(fieldToccoDataJSON.paths.registration_internet_from?.value)
      if (isAfter(registrationDateFrom, new Date())) {
        enabled = false
      }
    }
    if (fieldToccoDataJSON.paths.registration_internet_till?.value) {
      const registrationDateTill = parseToccoDate(fieldToccoDataJSON.paths.registration_internet_till?.value)
      if (isAfter(new Date(), registrationDateTill)) {
        enabled = false
      }
    }
  }
  return enabled
}

/**
 * Get the registration URL for a Tocco entity
 *
 * @param fieldToccoDataJSON The parsed Tocco data JSON
 * @param entityId The entity ID
 * @param isEnabled Whether registration is enabled
 * @returns The registration URL
 */
export function getRegistrationUrl(
  fieldToccoDataJSON: ToccoFieldData,
  entityId: number | string,
  isEnabled: boolean,
): string {
  return fieldToccoDataJSON.paths.links?.value
    ? fieldToccoDataJSON.paths.links?.value
    : isEnabled
      ? `/registration/module/${entityId}`
      : ''
}

/**
 * Check if a module run is currently open for registration
 *
 * @param run The module run with its registration window fields
 * @returns boolean indicating if the registration window is open
 */
export function isRunRegistrationOpen(run: {
  fieldRegistrationFrom?: { value?: string | null } | null
  fieldRegistrationTill?: { value?: string | null } | null
}): boolean {
  const from = run.fieldRegistrationFrom?.value
  const till = run.fieldRegistrationTill?.value
  if (!from && !till) return false
  if (from && isAfter(parseToccoDate(from), new Date())) return false
  if (till && isAfter(new Date(), parseToccoDate(till))) return false
  return true
}

/**
 * Normalize a Tocco duration-days value to a positive number
 *
 * The Drupal field migrates from integer to decimal (GraphQL scalar Decimal,
 * serialized as a string like "0.50"); Elasticsearch still delivers numbers.
 * Zero and unparseable values count as "no duration" (CARW-1701).
 *
 * @param value The raw duration value from GraphQL or Elasticsearch
 * @returns The numeric duration, or null when absent/zero/invalid
 */
export function normalizeDurationDays(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/**
 * Check whether a teaching method reference resolves to e-learning
 *
 * @param teachingMethod The teaching method reference field
 * @returns boolean indicating whether the e-learning presentation applies
 */
export function isELearningTeachingMethod(
  teachingMethod?: { entity?: { __typename?: string; fieldEnvironment?: string | null } | null } | null,
): boolean {
  return teachingMethod?.entity?.fieldEnvironment === 'e-learning'
}

/**
 * Format a Tocco price as CHF with apostrophe thousands separators
 *
 * @param price The raw price value (number or numeric string)
 * @returns The formatted price, e.g. "CHF 3'500", or '' when not set
 */
export function formatToccoPrice(price: number | string | null | undefined): string {
  if (price === null || price === undefined || price === '') return ''
  const numeric = parseFloat(String(price))
  if (isNaN(numeric)) return ''
  const [integerPart = '0', decimalPart = '00'] = numeric.toFixed(2).split('.')
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'")
  return decimalPart === '00' ? `CHF ${formattedInteger}` : `CHF ${formattedInteger}.${decimalPart}`
}

/**
 * Map a Tocco user to the Employee shape used by person teasers/grids
 *
 * @param user The Tocco user entity
 * @param isLecturer Use the lecturer function as position instead of the job
 * @returns The Employee data
 */
export function processToccoUser(user: ToccoUser, isLecturer = false): Employee {
  const academicTitle = user?.fieldAcademicTitle ? `${user.fieldAcademicTitle} ` : ''
  const firstName = user?.fieldFirstName ? `${user.fieldFirstName} ` : ''
  const lastName = user?.fieldLastName ? `${user.fieldLastName} ` : ''
  return {
    image: (user?.fieldProfilePicture?.entity as MediaImageFragment) || {
      fieldMediaImage: { url: '/tocco-user-placeholder.png', height: 135, width: 135 },
    },
    name: `${academicTitle}${firstName}${lastName}`.trim(),
    position: (isLecturer ? user?.fieldLecturerFunction : user?.fieldJob) ?? '',
  }
}
